export interface ExtractedColorPalette {
  dominantColor: string;
  palette: string[];
  suggestedPrimary: string;
  suggestedSecondary: string;
  isLight: boolean;
}

export interface GenieBranding {
  primaryColor: string;
  secondaryColor?: string | null;
  /** Standalone brand accent color — independent of the primary/secondary gradient pair */
  accentColor?: string | null;
  backgroundColor?: string | null;
  textColor?: string | null;
  /** Brand name (e.g., "WelcomeWheels") — stored here for self-contained brand identity */
  brandName?: string | null;
  /** Brand tagline (e.g., "Your Journey Begins Here") */
  tagline?: string | null;
  /** Primary brand logo - used everywhere (avatar, QR codes, embeds, etc.) */
  logoUrl?: string | null;
  /** @deprecated Use logoUrl instead. Kept for backwards compatibility only. */
  customAvatarUrl?: string | null;
  qrCodeStyle?: QRCodeStyle;
  embedStyle?: EmbedStyle;
  signatureStyle?: SignatureStyle;
  /** Hide the "Powered by Help Genie" attribution in the widget/embed UI */
  hideAttribution?: boolean;
  gradientEnabled?: boolean;
  gradientAngle?: number;
  /** Primary-colour strength of the avatar background, 0–100 (100 = full solid
   *  colour). Unset keeps the default light tint (28%). */
  avatarBackgroundOpacity?: number | null;
  extractedPalette?: ExtractedColorPalette | null;
  /** Metadata about who last saved this branding — set server-side, ignored if sent from client */
  lastUpdatedBy?: string | null;
  lastUpdatedByName?: string | null;
  lastUpdatedAt?: string | null;
}

export interface QRCodeStyle {
  foregroundColor: string;
  backgroundColor: string;
  cornerStyle: 'square' | 'rounded' | 'dots';
  logoUrl?: string | null;
  logoSize?: number;
}

export type WidgetPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export type BackgroundStyleType = 'solid' | 'gradient' | 'pattern';
export type BackgroundPatternId = 'dots' | 'grid' | 'diagonal-lines';

/** Popup body background — solid color, 2-color gradient, or a curated CSS pattern. */
export interface WidgetBackgroundStyle {
  type: BackgroundStyleType;
  /** Solid color, or gradient start color, or pattern base color. */
  color: string;
  /** Second color — used when type is 'gradient', optional accent for 'pattern'. */
  color2?: string | null;
  /** Gradient angle in degrees. Defaults to 135. */
  angle?: number;
  /** Which preset pattern to render when type === 'pattern'. */
  patternId?: BackgroundPatternId;
  /** Pattern tile size in px (dot/grid spacing, diagonal-line pitch).
   *  Defaults to the pattern's own preset size (14 for dots, 20 for grid,
   *  12 for diagonal-lines) when unset. */
  patternSize?: number;
}

/** Start Conversation CTA color/text overrides. Shape is read from `EmbedStyle.buttonStyle`. */
export interface WidgetButtonStyle {
  /** Solid color, or gradient start when color2 is set. Falls back to brandColor when unset. */
  color?: string | null;
  /** Optional second color — renders a 2-color gradient CTA instead of solid. */
  color2?: string | null;
  /** Explicit text color override. Falls back to the existing white/brandColor logic when unset. */
  textColor?: string | null;
  /** Multiplier applied to the button's font size, padding, and min-width.
   *  1 = default size. Range 0.7-1.6 covers "noticeably smaller" to
   *  "noticeably larger". */
  sizeScale?: number;
}

/** Chat button color/text overrides. Outlined (not filled) by default, so it
 *  takes a border color rather than a background/gradient — distinct from
 *  WidgetButtonStyle, which fills the Talk button solid or gradient. Shape
 *  is shared with the Talk button, read from `EmbedStyle.buttonStyle`. */
export interface WidgetChatButtonStyle {
  /** Border + icon + text color. Falls back to a neutral gray border/text. */
  color?: string | null;
  /** Optional background fill — leave unset to keep it outlined/transparent. */
  backgroundColor?: string | null;
  /** Multiplier applied to the button's font size, padding, and min-width.
   *  1 = default size, same range as WidgetButtonStyle.sizeScale. */
  sizeScale?: number;
}

/** Mic/chat/mute/etc action button (MagicalIconButton) color overrides. */
export interface WidgetActionButtonStyle {
  /** Idle background override (replaces the default `${brandColor}14`). */
  idleBackgroundColor?: string | null;
  /** Active/pressed-state background override (replaces the default solid brandColor). */
  activeBackgroundColor?: string | null;
  /** Icon color override (replaces brandColor for idle/hover icon color). */
  iconColor?: string | null;
  /** Multiplier applied to every action button's diameter and its icon glyph,
   *  keeping their ratio fixed. 1 = default size (36px row buttons, 48px chat
   *  button). Range 0.7-1.6 covers "noticeably smaller" to "noticeably larger",
   *  same range as WidgetButtonStyle.sizeScale. */
  sizeScale?: number;
}

export type ChatEmptyStateAlign = 'left' | 'center' | 'right';
export type ChatEmptyStateElement = 'icon' | 'title' | 'subtitle';

/** Styling for the widget popup's "Type instead" chat view empty state
 *  (shown before any messages exist) and the chat input surface. */
export interface WidgetChatStyle {
  /** Input pill background color override. Falls back to white. */
  inputBackgroundColor?: string | null;
  /** Hide the icon / title / subtitle lines of the empty state independently. */
  hiddenElements?: ChatEmptyStateElement[];
  /** Top-to-bottom render order of the 3 empty-state elements. Defaults to
   *  ['icon', 'title', 'subtitle']. */
  elementOrder?: ChatEmptyStateElement[];
  /** Horizontal alignment for each empty-state element. Defaults to 'center'. */
  elementAlign?: Partial<Record<ChatEmptyStateElement, ChatEmptyStateAlign>>;
  /** Text override for the title line. Falls back to "Ask {agentName} anything". */
  titleText?: string | null;
  /** Text override for the subtitle line. Falls back to the default hint copy. */
  subtitleText?: string | null;
  /** Input pill's gradient border color override. Falls back to the
   *  brandColor → secondaryColor gradient. Only meaningful when
   *  showBorder is not explicitly false (border is on by default). */
  borderColor?: string | null;
  /** Input pill's gradient border end color. Falls back to secondaryColor/derived accent. */
  borderColor2?: string | null;
  /** Hides the input pill's gradient border entirely. Default false (border shown). */
  hideBorder?: boolean;
  /** Placeholder text color override. Falls back to the default gray. */
  placeholderColor?: string | null;
  /** Plus/attach button background + icon color override. Falls back to a
   *  soft brand tint. */
  plusButtonColor?: string | null;
  /** When true, the send button reuses `EmbedStyle.actionButtons`
   *  (idle/active background + icon color) instead of the default brand
   *  gradient fill. */
  sendButtonUseActionStyle?: boolean;
}

export type WidgetCaptionSize = 'small' | 'medium' | 'large';

/** Caption bubble shown near the closed floating button, inviting the
 *  visitor to open the widget (e.g. "Got a question? Just ask"). */
export interface WidgetCaptionStyle {
  /** Whether the caption bubble is shown at all. Default false. */
  enabled?: boolean;
  /** Copy shown in the bubble. Falls back to "Got a question? Just ask". */
  text?: string;
  /** Bubble font size / padding preset. Defaults to 'medium'. */
  size?: WidgetCaptionSize;
  /** Bubble background color. Falls back to white. */
  backgroundColor?: string | null;
  /** Bubble text color. Falls back to a dark gray. */
  textColor?: string | null;
  /** Milliseconds after page load before the bubble first appears. Defaults to 4000. */
  delayMs?: number;
  /** Milliseconds the bubble stays visible before auto-hiding, or 0 to keep it shown indefinitely. Defaults to 8000. */
  autoHideMs?: number;
}

/** Popup header bar color/transparency overrides. Defaults to the
 *  brandColor → secondaryColor gradient the header already uses. */
export interface WidgetHeaderStyle {
  /** Solid color, or gradient start when color2 is set. Falls back to brandColor. */
  color?: string | null;
  /** Gradient end color. Falls back to secondaryColor/derived accent. */
  color2?: string | null;
  /** Renders the header with no fill so the popup body background shows through. */
  transparent?: boolean;
}

/** Screen edge (desktop) the Links & Resources tab docks to, at vertical
 *  center — mirrors `WidgetPosition`'s corner docking but only left/right
 *  make sense here (the rail vertically centers itself already).
 *  'bottom-center' pins the element to the bottom of the viewport instead,
 *  centered horizontally. 'hidden' removes the affordance entirely. */
export type FullPageRailPosition = 'left' | 'right' | 'bottom-center' | 'hidden';

/** Horizontal alignment of the centered avatar/CTA column within the page. */
export type FullPageAvatarAlign = 'center' | 'left' | 'right';

/** Where the welcome greeting line renders relative to the avatar. */
export type FullPageWelcomePosition = 'above-avatar' | 'below-avatar' | 'hidden';

export const RAIL_POSITION_OPTIONS: { value: FullPageRailPosition; label: string }[] = [
  { value: 'left', label: 'Left edge' },
  { value: 'right', label: 'Right edge' },
  { value: 'bottom-center', label: 'Bottom, centered' },
  { value: 'hidden', label: 'Hidden' },
];

export const AVATAR_ALIGN_OPTIONS: { value: FullPageAvatarAlign; label: string }[] = [
  { value: 'center', label: 'Center' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
];

export const WELCOME_POSITION_OPTIONS: { value: FullPageWelcomePosition; label: string }[] = [
  { value: 'above-avatar', label: 'Above avatar' },
  { value: 'below-avatar', label: 'Below avatar' },
  { value: 'hidden', label: 'Hidden' },
];

/** Where the transcript/chat column renders relative to the avatar column
 *  once a conversation's text view is open. 'left'/'right' place it as a
 *  side-by-side column (mirroring today's fixed right-hand placement).
 *  'bottom' stacks it as a full-width row below the avatar instead.
 *  'overlay' reuses the existing chat-expanded surface so the transcript
 *  takes over the screen rather than sitting beside the avatar. */
export type FullPageTranscriptPosition = 'left' | 'right' | 'bottom' | 'overlay';

export const TRANSCRIPT_POSITION_OPTIONS: { value: FullPageTranscriptPosition; label: string; description: string }[] = [
  { value: 'right', label: 'Right of avatar', description: 'Today\'s default — side-by-side column' },
  { value: 'left', label: 'Left of avatar', description: 'Mirrored — transcript on the left, avatar on the right' },
  { value: 'bottom', label: 'Below avatar', description: 'Full-width row underneath the avatar column' },
  { value: 'overlay', label: 'Overlay', description: 'Takes over the screen instead of sitting beside the avatar' },
];

/** Independent per-element placement for the full-page layout's pieces —
 *  genie avatar/buttons, Links & Resources tab, text (name + welcome
 *  message), and the transcript/chat area. Each element picks from a small
 *  set of valid docking points rather than freeform coordinates — the
 *  surrounding layout (centered flex column, fixed-edge rail, grid-based
 *  transcript column) is built around those docking points, not arbitrary
 *  positioning. */
export interface FullPageLayout {
  /** Links & Resources tab. Defaults to 'right'. */
  linksResourcesPosition?: FullPageRailPosition;
  /** Horizontal alignment of the avatar + active-buttons column. Defaults to 'center'. */
  avatarAlign?: FullPageAvatarAlign;
  /** Where the welcome greeting renders. Defaults to 'above-avatar'. */
  welcomePosition?: FullPageWelcomePosition;
  /** Where the transcript/chat column docks once opened. Defaults to 'right'. */
  transcriptPosition?: FullPageTranscriptPosition;
}

/** A few ready-made compositions of `FullPageLayout` — each varies several
 *  elements together (avatar alignment, text placement, rail docking,
 *  transcript docking), not just one rail's corner — offered as one-click
 *  starting points in the UI. Selecting one just fills in the individual
 *  fields above — it isn't a separate stored mode, so any field can still be
 *  tweaked afterward without falling out of "preset" state. */
export const FULL_PAGE_LAYOUT_PRESETS: { id: string; label: string; description: string; layout: FullPageLayout }[] = [
  {
    id: 'classic',
    label: 'Classic',
    description: 'Centered avatar and text, Links & Resources on the right, transcript opens to the right',
    layout: {
      linksResourcesPosition: 'right',
      avatarAlign: 'center',
      welcomePosition: 'above-avatar',
      transcriptPosition: 'right',
    },
  },
  {
    id: 'centered-focus',
    label: 'Centered Focus',
    description: 'Everything centered with the rail hidden, transcript opens as a full overlay — minimal, distraction-free chrome',
    layout: {
      linksResourcesPosition: 'hidden',
      avatarAlign: 'center',
      welcomePosition: 'above-avatar',
      transcriptPosition: 'overlay',
    },
  },
  {
    id: 'split',
    label: 'Split',
    description: 'Avatar and text shifted left with the rail on the right edge, transcript opens beside it on the right — a two-zone layout',
    layout: {
      linksResourcesPosition: 'right',
      avatarAlign: 'left',
      welcomePosition: 'above-avatar',
      transcriptPosition: 'right',
    },
  },
  {
    id: 'conversation-first',
    label: 'Conversation First',
    description: 'Compact avatar with welcome text below it, transcript opens underneath as a full-width row so the conversation reads top-to-bottom',
    layout: {
      linksResourcesPosition: 'right',
      avatarAlign: 'center',
      welcomePosition: 'below-avatar',
      transcriptPosition: 'bottom',
    },
  },
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'Hides the floating rail and the welcome line — avatar and name only, transcript opens as a full overlay',
    layout: {
      linksResourcesPosition: 'hidden',
      avatarAlign: 'center',
      welcomePosition: 'hidden',
      transcriptPosition: 'overlay',
    },
  },
];

/** Style/visibility/layout config for the standalone full-page Genie
 *  experience. Chat appearance is shared by Live Agent and Studio;
 *  voice layout/visibility controls are currently Studio-only. Kept as
 *  its own namespace, separate from the popup widget's `background`/
 *  `ctaButton`/etc, since the two surfaces are styled independently. */
export interface FullPageStyle {
  /** Page background — solid/gradient. Supersedes the legacy standalone
   *  `fullPageBackground` field, which is folded in here as this field. */
  background?: WidgetBackgroundStyle | null;
  /** Hides the greeting line shown above the avatar/start screen. Kept for
   *  back-compat with layouts saved before `layout.welcomePosition` existed;
   *  `layout.welcomePosition === 'hidden'` is the current source of truth. */
  hideWelcomeMessage?: boolean;
  /** Per-element placement for the floating rail, avatar column, and welcome text. */
  layout?: FullPageLayout;
  /** Custom photo shown as the big central avatar on the full-page start
   *  screen. Fills the circle (object-fit: cover) — distinct from the
   *  genie's logo/branding image, which stays a small mark near the name.
   *  Mirrors the popup's `EmbedStyle.avatarImageUrl`. */
  avatarImageUrl?: string | null;
  /** When true (and avatarImageUrl is set), shows the genie's logo as a
   *  small badge on top of the uploaded avatar photo. */
  avatarShowLogoOverlay?: boolean;
  /** Scale multiplier for the avatar circle. 1 = default size.
   *  Range 0.6–1.6 covers "noticeably smaller" to "noticeably larger". */
  avatarSizeScale?: number;
  /** Start Conversation CTA color/text overrides, reusing the popup's shape. */
  ctaButton?: WidgetButtonStyle | null;
  /** Chat button color/text overrides, reusing the popup's shape. */
  chatButton?: WidgetChatButtonStyle | null;
  /** Mic/chat/mute/etc action button color overrides, reusing the popup's shape. */
  actionButtons?: WidgetActionButtonStyle | null;
  /** Welcome message text color override. Falls back to the default page text color. */
  welcomeTextColor?: string | null;
  /** Website chat styling (Live Agent and Studio) — input background and empty-state
   *  (icon/title/subtitle) visibility, order, alignment, and copy. Reuses the
   *  popup's WidgetChatStyle shape. */
  chatStyle?: WidgetChatStyle | null;
}

/**
 * "Count chat leads as ad conversions". While enabled the genie carries the
 * requestContactDetails tool (handle-agent's syncContactDetailsTool), and each
 * captured lead tells the customer's page which tags to fire
 * (src/lib/leadConversions.ts → widget.js / widget-v2.js).
 */
export interface LeadConversions {
  enabled: boolean;
  /** Google Ads conversion target, "AW-XXXXXXXXX/label". */
  googleSendTo?: string | null;
  /** Fire Meta's standard Lead event. On unless switched off. */
  metaLead?: boolean;
}

export interface EmbedStyle {
  /** Accept public questions into this Genie's provisioned mailbox. Opt-in. */
  emailWidgetEnabled?: boolean;
  leadConversions?: LeadConversions;
  borderRadius?: number;
  shadowEnabled?: boolean;
  fontFamily?: string;
  headerColor?: string | null;
  buttonStyle?: 'rounded' | 'square' | 'pill';
  /** Selected widget template ID */
  widgetTemplate?: 'classic' | 'compact' | 'immersive' | 'pill-button' | 'side-tab' | 'banner' | 'glassmorphism' | 'dark-mode' | 'magic-v2';
  /** Screen corner the popup widget's floating button docks to. Defaults to 'bottom-right'. */
  widgetPosition?: WidgetPosition;
  /** Custom label shown on/near the floating button. Overrides the template's default label text. */
  widgetButtonLabel?: string | null;
  /** Background color override for the popup widget's body/header. Defaults to white. */
  widgetBackgroundColor?: string | null;
  /** Hides the agent description/subtitle line shown in the popup widget's header. */
  widgetHideWelcomeText?: boolean;
  /** Hides the greeting line shown above the avatar/start screen (e.g. "Hi
   *  there! Ready to...") — distinct from widgetHideWelcomeText, which only
   *  controls the header's description subtitle. */
  hideWelcomeMessage?: boolean;
  /** Custom photo shown as the big central avatar on the popup's start screen.
   *  Fills the circle (object-fit: cover) — distinct from the genie's
   *  logo/branding image, which stays a small mark in the header. */
  avatarImageUrl?: string | null;
  /** When true (and avatarImageUrl is set), shows the genie's logo as a small
   *  badge on top of the uploaded avatar photo. */
  avatarShowLogoOverlay?: boolean;
  /** Scale multiplier for the avatar circle, applied on top of the default
   *  size at each surface (embed popup, standalone page). 1 = default size.
   *  Range 0.6–1.6 covers "noticeably smaller" to "noticeably larger". */
  avatarSizeScale?: number;
  /** Popup body background — solid/gradient/pattern preset. Supersedes
   *  widgetBackgroundColor for new configs; widgetBackgroundColor is still read
   *  as a legacy fallback when this is unset. */
  background?: WidgetBackgroundStyle | null;
  /** @deprecated Background for the standalone full-page embed / live-agent
   *  page. Superseded by `fullPageStyle.background`. Kept only because the
   *  legacy live-agent page (src/pages/agent-interfaces/LiveAgentPage.tsx)
   *  still reads this field directly and is intentionally not being
   *  migrated onto `fullPageStyle` yet. Do not write new values here — write
   *  `fullPageStyle.background` instead. */
  fullPageBackground?: WidgetBackgroundStyle | null;
  /** Style/visibility/layout config for the standalone full-page Genie
   *  experience — separate from `background`/`ctaButton`/etc (the popup
   *  widget's own styling) since the two surfaces are styled independently.
   *  Currently read only by the Studio page (src/pages/genie/studio), never
   *  by the legacy live-agent page. Its `background` field supersedes the
   *  deprecated `fullPageBackground` above for anything written going
   *  forward. */
  fullPageStyle?: FullPageStyle | null;
  /** Header logo size in px, for the small genie mark shown above the avatar
   *  and chat screens. Defaults to 36. */
  headerLogoSize?: number;
  /** Popup header bar color/gradient/transparency overrides. */
  headerStyle?: WidgetHeaderStyle | null;
  /** Start Conversation CTA color/text overrides. Shape reuses `buttonStyle`. */
  ctaButton?: WidgetButtonStyle | null;
  /** Chat button color/text overrides. Shape reuses `buttonStyle`. */
  chatButton?: WidgetChatButtonStyle | null;
  /** Mic/chat/mute/etc action button color overrides. */
  actionButtons?: WidgetActionButtonStyle | null;
  /** "Type instead" chat view styling — input background color and empty-state
   *  (icon/title/subtitle) visibility, order, alignment, and copy. */
  chatStyle?: WidgetChatStyle | null;
  /** Caption bubble shown near the closed floating button, inviting the
   *  visitor to open the widget (e.g. "Got a question? Just ask"). */
  caption?: WidgetCaptionStyle | null;
  /** Custom overrides to the template configuration */
  widgetConfig?: {
    button?: {
      shape?: 'circle' | 'rounded-square' | 'pill' | 'square';
      size?: 'small' | 'medium' | 'large';
      showOnlineIndicator?: boolean;
      showLabel?: boolean;
      labelText?: string;
    };
    popup?: {
      size?: 'small' | 'medium' | 'large';
      borderRadius?: number;
      animation?: 'fade' | 'slide' | 'scale' | 'none';
    };
    theme?: {
      style?: 'light' | 'dark' | 'auto';
      glassEffect?: boolean;
    };
  };
}

/** Named layout presets for the branded email signature (HG Mail). Each maps to a
 *  fixed, email-client-safe table structure — not freeform positioning — so every
 *  combination of section toggles below still renders correctly in Outlook/Gmail/Apple Mail. */
export type SignatureLayout = 'standard' | 'compact' | 'centered';

export interface SignatureStyle {
  /** Master switch. When false, HG Mail falls back to the plain account signature,
   *  and the "Email Signature" block in report emails renders nothing. */
  enabled: boolean;
  layout: SignatureLayout;
  /** Show the description/tagline line under the genie name. Text itself comes from
   *  the genie's description, unless textOverride is set. */
  showDescription: boolean;
  /** Show the Help Genie mascot artwork. */
  showMascot: boolean;
  /** Show the "Scan to chat" QR code. */
  showQrCode: boolean;
  /** Show the row of contact channel badges (Chat/Call/Web). */
  showChannelBadges: boolean;
  /** Optional text shown instead of the genie's own description. Null/empty uses the default. */
  textOverride?: string | null;
  /**
   * Snapshot of this genie's contact channels, refreshed every time branding is saved
   * (BrandingModal's handleSave). Every renderer of this signature — HG Mail's reply
   * footer and the report-email "Email Signature" block — reads these fields only,
   * with no live lookup of its own, so the signature is self-contained on `branding`
   * and never has to reach into agent/account tables it may not have loaded.
   */
  phoneNumber?: string | null;
  liveUrl?: string | null;
}

export const DEFAULT_SIGNATURE_STYLE: SignatureStyle = {
  enabled: true,
  layout: 'standard',
  showDescription: true,
  showMascot: true,
  showQrCode: true,
  showChannelBadges: true,
  textOverride: null,
  phoneNumber: null,
  liveUrl: null,
};

export const SIGNATURE_LAYOUT_OPTIONS: { value: SignatureLayout; label: string; description: string }[] = [
  { value: 'standard', label: 'Standard', description: 'Logo left, name and contact badges, mascot and QR on the right' },
  { value: 'compact', label: 'Compact', description: 'Logo, name, and contact badges only — no QR or mascot artwork' },
  { value: 'centered', label: 'Centered', description: 'Everything stacked and centered, for narrow layouts' },
];

/** QR code display style presets */
export type QRDisplayStyle = 'framed' | 'minimal' | 'card' | 'clean';

/** QR code label options */
export type QRLabelOption = 'scan-to-connect' | 'scan-to-chat' | 'talk-to-genie' | 'none';

/** QR code download format */
export type QRDownloadFormat = 'png' | 'svg';

/** Configuration for QR code display in the modal */
export interface QRDisplayConfig {
  style: QRDisplayStyle;
  label: QRLabelOption;
  transparentBackground: boolean;
  showLogo: boolean;
  foregroundColor: string;
  backgroundColor: string;
}

export const DEFAULT_QR_DISPLAY_CONFIG: QRDisplayConfig = {
  style: 'framed',
  label: 'scan-to-connect',
  transparentBackground: false,
  showLogo: true,
  foregroundColor: '#000000',
  backgroundColor: '#FFFFFF',
};

/** Preset color schemes for QR codes */
export const QR_COLOR_PRESETS: { name: string; foreground: string; background: string }[] = [
  { name: 'Classic', foreground: '#000000', background: '#FFFFFF' },
  { name: 'Inverted', foreground: '#FFFFFF', background: '#000000' },
  { name: 'Navy', foreground: '#1e3a5f', background: '#FFFFFF' },
  { name: 'Forest', foreground: '#1a472a', background: '#FFFFFF' },
  { name: 'Burgundy', foreground: '#722f37', background: '#FFFFFF' },
  { name: 'Slate', foreground: '#475569', background: '#f8fafc' },
];

export const QR_STYLE_OPTIONS: { value: QRDisplayStyle; label: string; description: string }[] = [
  { value: 'framed', label: 'Framed', description: 'Gradient background with decorative corners' },
  { value: 'minimal', label: 'Minimal', description: 'Clean QR code with logo only' },
  { value: 'card', label: 'Card', description: 'QR code with genie name below' },
  { value: 'clean', label: 'Clean', description: 'White background with subtle border' },
];

export const QR_LABEL_OPTIONS: { value: QRLabelOption; label: string }[] = [
  { value: 'scan-to-connect', label: 'Scan to Connect' },
  { value: 'scan-to-chat', label: 'Scan to Chat' },
  { value: 'talk-to-genie', label: 'Talk to [Genie Name]' },
  { value: 'none', label: 'No Label' },
];

export const DEFAULT_BRANDING: GenieBranding = {
  primaryColor: '#4E9CFF',
  secondaryColor: null,
  accentColor: null,
  backgroundColor: null,
  textColor: null,
  logoUrl: null,
  customAvatarUrl: null,
  gradientEnabled: false,
  gradientAngle: 135,
  qrCodeStyle: {
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    cornerStyle: 'square',
    logoUrl: null,
    logoSize: 50
  },
  embedStyle: {
    borderRadius: 8,
    shadowEnabled: true,
    fontFamily: 'system-ui',
    headerColor: null,
    buttonStyle: 'rounded',
    widgetTemplate: 'classic',
    headerLogoSize: 36,
  },
  signatureStyle: DEFAULT_SIGNATURE_STYLE,
};
